// Standalone "How Cache values your item" screen (GET-266). Registered on the
// root stack outside the signed-in check, so guest (Appraise Result) and
// signed-in screens can both open it with
// `navigation.navigate('CacheValueExplainer')`. Presented as a modal: a
// sheet-style card on iOS, a normal page at /cache-value on web.
//
// The owner chose a separate screen over a bottom-sheet library on
// 2026-11-05 (GET-266 T8): @gorhom/bottom-sheet didn't present reliably on
// the webpack web build.

import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { CacheValueExplainer } from '../components/common/CacheValueExplainer';
import type { RootStackParamList } from '../types/navigation';

type Props = NativeStackScreenProps<RootStackParamList, 'CacheValueExplainer'>;

export default function CacheValueExplainerScreen({ navigation }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const { session } = useAuth();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        closeRow: {
          flexDirection: 'row',
          justifyContent: 'flex-end',
          paddingHorizontal: theme.spacing.sm,
          paddingTop: theme.spacing.sm,
        },
        // 44pt touch target around a 24pt icon.
        closeButton: {
          width: 44,
          height: 44,
          alignItems: 'center',
          justifyContent: 'center',
        },
        content: {
          paddingHorizontal: theme.spacing.lg,
          paddingBottom: theme.spacing.xxl,
        },
      }),
    [theme]
  );

  // A web visitor can land on /cache-value directly, with nothing to go back
  // to. Replace the explainer rather than push on top of it, so browser back
  // doesn't return here.
  const close = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.replace(session ? 'Main' : 'Landing');
    }
  };

  return (
    <SafeAreaView style={commonStyles.safeArea} edges={['top', 'bottom']}>
      <View style={styles.closeRow}>
        <TouchableOpacity
          testID="cache-value-explainer-close"
          style={styles.closeButton}
          onPress={close}
          accessibilityRole="button"
          accessibilityLabel="Close"
        >
          <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
      </View>
      <ScrollView contentContainerStyle={styles.content}>
        <CacheValueExplainer />
      </ScrollView>
    </SafeAreaView>
  );
}
